import React, { useState, useCallback, useRef, useEffect } from "react";
import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { Box, Skeleton, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTheme } from "@mui/material/styles";
import { ResizeHandle } from "./ResizeHandle";
import { ImageAlignmentToolbar } from "./ImageAlignmentToolbar";
import {
  ImageAlignment,
  ResizeHandlePosition,
  ResizeState,
} from "./resizableImage.types";

const MIN_WIDTH = 100;
const MAX_WIDTH = 800;

export const ResizableImageNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  editor,
}) => {
  const theme = useTheme();
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);

  const {
    src,
    alt,
    title,
    width,
    height,
    alignment,
    aspectRatio,
    isLoading,
    errorMessage,
  } = node.attrs;

  const isEditable = editor.isEditable;

  // Calculate aspect ratio on image load
  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (!aspectRatio && img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        updateAttributes({ aspectRatio: ratio });

        // Set initial width if not set
        if (width === "auto" || !width) {
          const initialWidth = Math.min(img.naturalWidth, MAX_WIDTH);
          updateAttributes({
            width: initialWidth,
            height: initialWidth / ratio,
          });
        }
      }
    },
    [aspectRatio, width, updateAttributes],
  );

  // Handle resize start
  const handleResizeStart = useCallback(
    (position: ResizeHandlePosition, e: React.MouseEvent) => {
      e.preventDefault();
      const currentWidth =
        typeof width === "number"
          ? width
          : imageRef.current?.offsetWidth || 300;
      const currentHeight =
        typeof height === "number"
          ? height
          : imageRef.current?.offsetHeight || 200;

      setResizeState({
        isResizing: true,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: currentWidth,
        startHeight: currentHeight,
        handle: position,
      });
    },
    [width, height],
  );

  // Handle touch resize start
  const handleTouchStart = useCallback(
    (position: ResizeHandlePosition, e: React.TouchEvent) => {
      const touch = e.touches[0];
      const currentWidth =
        typeof width === "number"
          ? width
          : imageRef.current?.offsetWidth || 300;
      const currentHeight =
        typeof height === "number"
          ? height
          : imageRef.current?.offsetHeight || 200;

      setResizeState({
        isResizing: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startWidth: currentWidth,
        startHeight: currentHeight,
        handle: position,
      });
    },
    [width, height],
  );

  // Handle resize move
  useEffect(() => {
    if (!resizeState?.isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeState) return;

      const deltaX = e.clientX - resizeState.startX;
      const ratio = aspectRatio || 1;

      // Calculate new width based on handle position
      let newWidth: number;
      if (
        resizeState.handle === "top-left" ||
        resizeState.handle === "bottom-left"
      ) {
        newWidth = resizeState.startWidth - deltaX;
      } else {
        newWidth = resizeState.startWidth + deltaX;
      }

      // Apply constraints
      newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));
      const newHeight = newWidth / ratio;

      updateAttributes({ width: newWidth, height: newHeight });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!resizeState) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - resizeState.startX;
      const ratio = aspectRatio || 1;

      let newWidth: number;
      if (
        resizeState.handle === "top-left" ||
        resizeState.handle === "bottom-left"
      ) {
        newWidth = resizeState.startWidth - deltaX;
      } else {
        newWidth = resizeState.startWidth + deltaX;
      }

      newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));
      const newHeight = newWidth / ratio;

      updateAttributes({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setResizeState(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [resizeState, aspectRatio, updateAttributes]);

  // Handle alignment change
  const handleAlignmentChange = useCallback(
    (newAlignment: ImageAlignment) => {
      updateAttributes({ alignment: newAlignment });
    },
    [updateAttributes],
  );

  // Handle image click
  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isEditable) {
        setShowToolbar(true);
      }
    },
    [isEditable],
  );

  // Close toolbar
  const handleCloseToolbar = useCallback(() => {
    setShowToolbar(false);
  }, []);

  // Alignment justify styles
  const getJustifyContent = (align: ImageAlignment) => {
    switch (align) {
      case "left":
        return "flex-start";
      case "right":
        return "flex-end";
      case "center":
      default:
        return "center";
    }
  };

  // Calculate display dimensions
  const displayWidth =
    typeof width === "number" ? `${width}px` : width || "auto";
  const displayHeight =
    typeof height === "number" ? `${height}px` : height || "auto";

  return (
    <NodeViewWrapper className="resizable-image-wrapper">
      <Box
        sx={{
          display: "flex",
          justifyContent: getJustifyContent(alignment),
          my: "16px",
          width: "100%",
        }}
      >
        <Box
          ref={containerRef}
          onClick={handleImageClick}
          sx={{
            position: "relative",
            display: "inline-block",
            cursor: isEditable ? "pointer" : "default",
            userSelect: resizeState?.isResizing ? "none" : "auto",
          }}
        >
          {/* Loading / Error overlay */}
          {(isLoading || errorMessage) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: errorMessage
                  ? theme.palette.error.light
                  : "rgba(255, 255, 255, 0.8)",
                borderRadius: "4px",
                zIndex: 5,
              }}
            >
              {errorMessage ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    px: "16px",
                  }}
                >
                  <ErrorOutlineIcon
                    sx={{
                      color: theme.palette.error.dark,
                      fontSize: "20px",
                    }}
                  />
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.error.dark }}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              ) : (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{ borderRadius: "4px" }}
                />
              )}
            </Box>
          )}

          {/* Main image */}
          <img
            ref={imageRef}
            src={src}
            alt={alt || ""}
            title={title || ""}
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              width: displayWidth,
              height: displayHeight,
              maxWidth: "100%",
              display: "block",
              borderRadius: "4px",
              outline: selected
                ? `1px solid ${theme.palette.primary.main}`
                : "none",
              outlineOffset: "2px",
              transition: "outline 0.15s ease",
            }}
          />

          {/* Resize handles - only show when selected and editable */}
          {selected && isEditable && !isLoading && !errorMessage && (
            <>
              <ResizeHandle
                position="top-left"
                onResizeStart={handleResizeStart}
                onTouchStart={handleTouchStart}
              />
              <ResizeHandle
                position="top-right"
                onResizeStart={handleResizeStart}
                onTouchStart={handleTouchStart}
              />
              <ResizeHandle
                position="bottom-left"
                onResizeStart={handleResizeStart}
                onTouchStart={handleTouchStart}
              />
              <ResizeHandle
                position="bottom-right"
                onResizeStart={handleResizeStart}
                onTouchStart={handleTouchStart}
              />
            </>
          )}

          {/* Alignment toolbar - shows on click */}
          {showToolbar && isEditable && !isLoading && !errorMessage && (
            <ImageAlignmentToolbar
              currentAlignment={alignment}
              onAlignmentChange={handleAlignmentChange}
              onClose={handleCloseToolbar}
            />
          )}
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};
